import React, { createContext, useContext, useState, useEffect } from 'react';
import { userAPI, User } from '../api/users';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('🔄 AuthProvider - Initialisation...');
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log('👤 Utilisateur trouvé dans localStorage:', {
          id: parsedUser.id,
          email: parsedUser.email,
          role: parsedUser.role
        });
        setUser(parsedUser);
      } catch (error) {
        console.error('❌ Erreur parsing localStorage user:', error);
        localStorage.removeItem('user');
      }
    } else {
      console.log('👤 Aucun utilisateur dans localStorage');
    }
    setIsLoading(false);
    console.log('✅ AuthProvider - Initialisation terminée');
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    console.log('🔑 AuthContext.login appelé');
    try {
      console.log('📞 Appel userAPI.login...');
      const response = await userAPI.login({ email, password });
      console.log('📦 Réponse userAPI.login:', response);

      console.log('💾 Sauvegarde utilisateur dans state et localStorage...');
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      console.log('✅ Utilisateur connecté et sauvegardé');

      console.log('🔄 Mise à jour de l\'état d\'authentification...');
      return true;
    } catch (error) {
      console.error('❌ Échec de la connexion dans AuthContext:', error);
      console.error('📋 Type d\'erreur:', typeof error);
      console.error('📊 Message:', error instanceof Error ? error.message : 'Erreur inconnue');
      throw error;
    }
  };

  const logout = () => {
    console.log('🚪 Déconnexion utilisateur...');
    setUser(null);
    localStorage.removeItem('user');
  };

  const isAuthenticated = !!user;

  console.log('🔍 AuthProvider - État actuel:', {
    user: user ? { id: user.id, email: user.email, role: user.role } : null,
    isLoading,
    isAuthenticated
  });

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      logout,
      isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
